/* eslint-disable import/exports-last */
/* eslint-disable @typescript-eslint/no-explicit-any */
import LogoItzGreyscale from '@betterplace/assets/images/logos/itz-greyscale.svg'
import OrgaLogo from '@betterplace/assets/images/logos/orga-logo.svg'
import TaxReceiptHint from './TaxReceiptHint/TaxReceiptHint'
import classNames from 'classnames'
import styles from './Footer.module.css'
import { DefaultImage, DefaultLink, LinkItem, type LinkItemProps } from '../shared'
import { Icon } from '../../components/Icon'
import { LayoutAccordion, LayoutAccordionItem } from '../../components/layouts/LayoutAccordion'
import { LayoutCenter, LayoutCluster, LayoutStack } from '../../components/layouts'
import { type ReactNode, useEffect, useState } from 'react'
import { getSafeImageSource } from '../../utils'
import { headlessLinkProps } from '../../components/TextLink'
import type { FooterProps, FooterText, Section } from './types'
const orgaLogo = getSafeImageSource(OrgaLogo)
const logoItz = getSafeImageSource(LogoItzGreyscale)

function getDomain() {
  const hostname = window.location.hostname
  if (!hostname) return ''
  const hostnameArray = hostname.split('.')
  if (hostnameArray.length <= 2) return hostname

  return hostnameArray.slice(1).join('.')
}

const onCookieSettingsLinkClick = () => {
  const domain = getDomain()
  const maxAge = new Date(2099, 1, 1).getTime()
  document.cookie = `betterplace-tracking-accepted=edit; path=/; max-age=${maxAge}; domain=.${domain}`
}

function mapSections(sections: Section[]) {
  if (!sections.length) return []
  const sorted = sections.length > 1 ? sections.sort(({ column: a }, { column: b }) => a - b) : sections
  let column = 0

  return sorted.map((section) => {
    column = section.column ?? column
    const res = {
      ...section,
      column: section.column ?? column,
    }
    column++
    return res
  })
}

function groupByColumn(sections: Section[]) {
  const grouped = sections.reduce(
    (acc, section) => {
      acc[section.column] = [...(acc[section.column] ?? []), section]
      return acc
    },
    {} as Record<number, Section[]>
  )

  return Object.values(grouped)
}

function FooterLinkItem({ ...props }: Omit<LinkItemProps, 'doNotOpenInNewTab'>) {
  const { className } = headlessLinkProps({
    kind: 'secondary',
    size: 'small',
    className: classNames(props.className, styles.footerLink),
  })
  return <LinkItem {...props} doNotOpenInNewTab className={className} />
}

function CookieSettingsLink({
  cookieSettingsLink,
  Link = DefaultLink,
  section,
}: {
  cookieSettingsLink: FooterText['cookieSettingsLink']
  Link?: React.FC<any>
  section: Section
}) {
  if (section.title !== cookieSettingsLink.section) return null
  return (
    <FooterLinkItem key="cookie" Link={Link} onClick={onCookieSettingsLinkClick} className={styles.cookieSettingsLink}>
      {cookieSettingsLink.label}
    </FooterLinkItem>
  )
}

export function FooterMobileSectionContent({
  children,
  Link = DefaultLink,
  links,
  mobile,
}: Pick<Section, 'links' | 'mobile'> & { Link?: React.FC<any>; children?: ReactNode }) {
  if (mobile) return mobile
  return (
    <LayoutStack space="200" className={styles.accordionContent}>
      {links.map((linkItemProps, index) => (
        <FooterLinkItem key={index} Link={Link} {...linkItemProps} />
      ))}
      {children}
    </LayoutStack>
  )
}

export function FooterDesktopSectionContent({
  links,
  Link = DefaultLink,
  desktop,
  children,
}: Pick<Section, 'links' | 'desktop'> & { Link?: React.FC<any>; children?: ReactNode }) {
  if (desktop) return desktop
  return (
    <>
      {links.map((linkItemProps, index) => (
        <FooterLinkItem key={index} Link={Link} {...linkItemProps} />
      ))}
      {children}
    </>
  )
}

function DesktopFooter({ text, Link = DefaultLink }: Pick<FooterProps, 'text' | 'Link'>) {
  const groups = groupByColumn(mapSections(text.sections))
  return (
    <nav className={styles.wideOnly}>
      <LayoutCluster className={styles.grid} flex space="300" justify="space-between" wrap>
        {groups.map((group, index) => {
          return (
            <LayoutStack key={index} className={styles.item} space="300">
              {group.map((section) => (
                <LayoutStack key={section.title}>
                  <p key="title">
                    <strong>{section.title}</strong>
                  </p>
                  <FooterDesktopSectionContent links={section.links} desktop={section.desktop} Link={Link}>
                    <CookieSettingsLink key="cookie" cookieSettingsLink={text.cookieSettingsLink} section={section} />
                  </FooterDesktopSectionContent>
                </LayoutStack>
              ))}
            </LayoutStack>
          )
        })}
      </LayoutCluster>
    </nav>
  )
}

function MobileFooter({ text, Link = DefaultLink }: Pick<FooterProps, 'text' | 'Link'>) {
  const defaultKey = ''
  const [selected, setSelected] = useState(defaultKey)
  return (
    <nav className={styles.mobileOnly}>
      <LayoutAccordion
        defaultKey={defaultKey}
        activeKey={selected}
        scrollOnOpen={false}
        space="none"
        animationSpeedInMs={300}
      >
        {text.sections.map((section) => (
          <LayoutAccordionItem
            key={section.title}
            id={section.title}
            onClick={(key) => setSelected((prev) => (key === prev ? '' : key))}
            label={<span className={styles.accordionLabel}>{section.title}</span>}
          >
            <FooterMobileSectionContent links={section.links} mobile={section.mobile} Link={Link}>
              <CookieSettingsLink key="cookie" cookieSettingsLink={text.cookieSettingsLink} section={section} />
            </FooterMobileSectionContent>
          </LayoutAccordionItem>
        ))}
      </LayoutAccordion>
    </nav>
  )
}

export function LocaleSwitchLink({
  children,
  href,
  className,
  Link = DefaultLink,
}: {
  children: string
  href: string
  className?: string
  Link?: React.FC<any>
}) {
  const [disabled, setDisabled] = useState(true)
  const [hidden, setHidden] = useState(false)
  useEffect(() => {
    setDisabled(!href)
    setHidden(!children)
  }, [href, children])
  if (hidden) return null
  return (
    <Link disabled={disabled} href={href} className={classNames(styles.localeSwitchLink, className)}>
      {children}
    </Link>
  )
}

function Footer({ text, Link = DefaultLink, Image = DefaultImage, localeSwitchLink }: FooterProps) {
  return (
    <LayoutCenter as="footer" className={classNames('bp-shared', styles.footer)} centeredChildren>
      <TaxReceiptHint label={text.taxReceiptHint.label} href={text.taxReceiptHint.href} Link={Link}>
        {text.taxReceiptHint.children}
      </TaxReceiptHint>
      <LayoutStack className={styles.footerContent} flex>
        <DesktopFooter text={text} Link={Link} />
        <MobileFooter text={text} Link={Link} />
        <Image src={orgaLogo} alt="" width={190} className={styles.logo} />
        <LayoutCluster alignment="intrinsic" justify="space-between" className={styles.textContent}>
          <LayoutStack className={styles.organizationInfo} flex>
            <p>
              <strong>{text.organizationInfo}</strong>
            </p>
            <p className={styles.organizationInfoDetails}>{text.organizationInfoDetails}</p>
          </LayoutStack>
          <LayoutStack className={styles.socialLinks}>
            <LayoutCluster justify="space-between" align="center">
              <LayoutCluster space="200" align="center">
                <LinkItem
                  key="facebook"
                  Link={Link}
                  href={text.facebookLink.href}
                  title={text.facebookLink.label}
                  external
                  className={styles.socialLink}
                >
                  <Icon name="facebook" size="400" aria-hidden="true" />
                  <span className="sr-only">{text.facebookLink.label}</span>
                </LinkItem>
                <LinkItem
                  key="instagram"
                  Link={Link}
                  href={text.instagramLink.href}
                  title={text.instagramLink.label}
                  external
                  className={styles.socialLink}
                >
                  <Icon name="instagram" size="400" aria-hidden="true" />
                  <span className="sr-only">{text.instagramLink.label}</span>
                </LinkItem>
                <LinkItem
                  key="linkedin"
                  Link={Link}
                  href={text.linkedinLink.href}
                  title={text.linkedinLink.label}
                  external
                  className={styles.socialLink}
                >
                  <Icon name="linkedin" size="400" aria-hidden="true" />
                  <span className="sr-only">{text.linkedinLink.label}</span>
                </LinkItem>
              </LayoutCluster>
              {localeSwitchLink}
            </LayoutCluster>
            <LinkItem
              Link={Link}
              href={text.transparentCivilSocietyLink.href}
              className={styles.transparentCivilSocietyLink}
            >
              <Image src={logoItz} alt={text.transparentCivilSocietyLink.label} width={240} />
            </LinkItem>
          </LayoutStack>
        </LayoutCluster>
      </LayoutStack>
    </LayoutCenter>
  )
}

export default Footer
